import { useState } from "react";
import { Loader2, Pause, Play } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "sonner";

export function PauseSubscriptionDialog({ open, onOpenChange, mode }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "pause" | "resume";
}) {
  const { refresh } = useSubscription();
  const [months, setMonths] = useState("1");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("subscription-pause", {
      body: {
        action: mode,
        months: parseInt(months),
        environment: getPaddleEnvironment(),
      },
    });
    setBusy(false);
    if (error || data?.error) {
      toast.error(mode === "pause" ? "Could not pause" : "Could not resume", {
        description: data?.error ?? error?.message,
      });
      return;
    }
    toast.success(mode === "pause" ? "Subscription paused" : "Subscription resumed");
    await refresh();
    onOpenChange(false);
  };

  if (mode === "resume") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-4 w-4 text-primary" /> Resume subscription
            </DialogTitle>
            <DialogDescription>
              Restart billing immediately and regain full dashboard access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Not now</Button>
            <Button onClick={submit} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              Resume now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pause className="h-4 w-4 text-primary" /> Pause subscription
          </DialogTitle>
          <DialogDescription>
            Stop billing without losing your data. We'll auto-resume at the end of the period you pick.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup value={months} onValueChange={setMonths} className="space-y-1.5">
          {[
            { v: "1", label: "1 month" },
            { v: "2", label: "2 months" },
            { v: "3", label: "3 months" },
          ].map((o) => (
            <label
              key={o.v}
              htmlFor={`pause-${o.v}`}
              className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                months === o.v ? "border-primary bg-primary/5" : "border-border hover:border-border/80"
              }`}
            >
              <RadioGroupItem value={o.v} id={`pause-${o.v}`} />
              <Label htmlFor={`pause-${o.v}`} className="cursor-pointer text-sm">{o.label}</Label>
            </label>
          ))}
        </RadioGroup>

        <p className="text-xs text-muted-foreground">
          Your dashboard becomes read-only while paused. Reps can still view past deals.
        </p>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Pause className="h-4 w-4 mr-2" />}
            Pause subscription
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}